import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { PHONE_TEL } from "../../lib/site";
import { Reveal, SectionTag } from "./Reveal";

const IMG = {
  copper: "https://images.unsplash.com/photo-1704265586389-32d7fd68e7d9?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  brass: "https://static.prod-images.emergentagent.com/jobs/293d0833-5c7d-4417-9605-d9260947d0b6/images/eccd51cfff73aac502057f5bd54ecc5ecb408d246fd1d12cadb02ce488b96d9e.jpeg",
  aluminum: "https://static.prod-images.emergentagent.com/jobs/293d0833-5c7d-4417-9605-d9260947d0b6/images/0e03e93eeeda775819c20bc9fd2283c9914fa3994c8ea815e21ed38f4422da9c.jpeg",
  aluminum_cans: "https://images.unsplash.com/photo-1561503412-852800622772?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  insulated_wire: "https://images.unsplash.com/photo-1761507320645-b11a00bfcc34?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  batteries: "https://images.unsplash.com/photo-1597766325363-f5576d851d6a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  stainless: "https://images.unsplash.com/photo-1697698532634-ea59b636ccea?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  motors: "https://images.unsplash.com/photo-1563456019498-843e11bdaae0?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  steel: "https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  cats: "https://images.unsplash.com/photo-1692309175422-b9d614f4764e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
};

const MATERIALS = [
  { name: "Copper", img: IMG.copper },
  { name: "Brass", img: IMG.brass },
  { name: "Aluminum", img: IMG.aluminum },
  { name: "Aluminum Cans", img: IMG.aluminum_cans },
  { name: "Insulated Wire", img: IMG.insulated_wire },
  { name: "Batteries", img: IMG.batteries },
  { name: "Stainless Steel", img: IMG.stainless },
  { name: "Electric Motors", img: IMG.motors },
  { name: "Ferrous / Steel", img: IMG.steel },
  { name: "Catalytic Converters", img: IMG.cats },
];

export const WhatWeBuy = () => (
  <section id="what-we-buy" className="bg-white py-20 sm:py-28" data-testid="what-we-buy-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <SectionTag number="01" label="Materials" />
        <h2 className="font-heading text-4xl sm:text-5xl text-zinc-950 tracking-tight">
          WHAT ARE YOU <span className="text-red-700">SCRAPPING?</span>
        </h2>
        <p className="mt-4 text-zinc-600 max-w-2xl">
          We buy many other ferrous and non-ferrous metals beyond the list below. Prices change with the market — call for today's pricing.
        </p>
      </Reveal>

      <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {MATERIALS.map((m, i) => (
          <motion.div
            key={m.name}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: (i % 5) * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className="group border border-zinc-200 bg-white hover:border-red-700 transition-colors"
            data-testid={`material-card-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            <div className="aspect-[4/3] overflow-hidden bg-zinc-100">
              <img
                src={m.img}
                alt={m.name}
                loading="lazy"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 duration-500"
                style={{ transitionProperty: "filter, transform" }}
              />
            </div>
            <div className="px-3 py-3 flex items-center justify-between">
              <span className="font-heading text-sm sm:text-base tracking-wide text-zinc-950">{m.name.toUpperCase()}</span>
              <span className="h-2 w-2 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </motion.div>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-12 border-2 border-zinc-950 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 bg-zinc-50">
          <div>
            <p className="font-heading text-2xl text-zinc-950 tracking-wide">NOT SURE WHAT YOU HAVE? CALL US.</p>
            <p className="text-zinc-600 mt-1 text-sm">We'll tell you exactly what it's worth today.</p>
          </div>
          <a
            href={PHONE_TEL}
            className="inline-flex items-center gap-3 bg-red-700 text-white px-6 py-3.5 font-heading tracking-wider hover:bg-red-800 transition-colors shrink-0"
            data-testid="what-we-buy-call-cta"
          >
            <Phone className="h-5 w-5" />
            CALL FOR TODAY'S PRICE
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);
